import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  FaTachometerAlt, 
  FaCalendarCheck, 
  FaUsers, 
  FaStar, 
  FaSignOutAlt,
  FaEnvelope,
  FaUserCircle,
  FaBars,
  FaTimes,
  FaClock,
  FaCheckCircle
} from 'react-icons/fa';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase/config';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate(); 
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({
    totalRendezVous: 0,
    enAttente: 0,
    aujourdhui: 0,
    avisNonValides: 0
  });

  useEffect(() => {
    const rendezvousRef = ref(database, 'rendezvous');
    const unsubscribe = onValue(rendezvousRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        let total = 0;
        let enAttente = 0;
        let aujourdhui = 0;
        const aujourdhuiDate = new Date().toISOString().split('T')[0];
        
        Object.values(data).forEach(userRdv => {
          Object.values(userRdv).forEach(rdv => {
            total++;
            if (rdv.statut === 'en attente') enAttente++;
            if (rdv.date === aujourdhuiDate) aujourdhui++;
          });
        });
        
        setStats(prev => ({ ...prev, totalRendezVous: total, enAttente, aujourdhui }));
      }
    });

    const avisRef = ref(database, 'avis');
    const unsubscribeAvis = onValue(avisRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const nonValides = Object.values(data).filter(a => !a.valide).length;
        setStats(prev => ({ ...prev, avisNonValides: nonValides }));
      }
    });

    return () => {
      unsubscribe();
      unsubscribeAvis();
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/'); 
    } catch (error) {
      console.error('Erreur lors de la déconnexion', error);
    }
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: <FaTachometerAlt />, label: 'Tableau de bord', badge: null },
    { path: '/admin/dashboard/rendezvous', icon: <FaCalendarCheck />, label: 'Rendez-vous', badge: stats.enAttente },
    { path: '/admin/dashboard/clients', icon: <FaUsers />, label: 'Clients', badge: null },
    { path: '/admin/dashboard/avis', icon: <FaStar />, label: 'Avis clients', badge: stats.avisNonValides }
    // La ligne "Contacter clients" a été supprimée
  ];

  const isActive = (path) => {
    if (path === '/admin/dashboard' && location.pathname === '/admin/dashboard') return true;
    if (path !== '/admin/dashboard' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const getPageTitle = () => {
    const currentItem = menuItems.find(item => isActive(item.path));
    return currentItem ? currentItem.label : 'Tableau de bord';
  };

  return (
    <div className="admin-dashboard-modern">
      {/* Sidebar */}
      <aside className={`admin-sidebar-modern ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <div className="admin-logo">
            <FaCalendarCheck className="admin-logo-icon" />
            <span>SMTD Admin</span>
          </div>
          <button className="admin-mobile-close" onClick={() => setIsMobileMenuOpen(false)}>
            <FaTimes />
          </button>
        </div>

        <div className="admin-profile">
          <div className="admin-profile-avatar">
            {currentUser?.email?.charAt(0).toUpperCase()}
          </div>
          <div className="admin-profile-info">
            <h4>{currentUser?.email?.split('@')[0]}</h4>
            <p>Administrateur</p>
          </div>
        </div>

        <nav className="admin-sidebar-nav">
          {menuItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              className={`admin-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="admin-nav-icon">{item.icon}</span>
              <span className="admin-nav-label">{item.label}</span>
              {item.badge > 0 && <span className="admin-nav-badge">{item.badge}</span>}
            </Link>
          ))}
          
          <div className="admin-nav-divider"></div>
          
          <Link to="/" className="admin-nav-item" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="admin-nav-icon"><FaUserCircle /></span>
            <span className="admin-nav-label">Voir le site</span>
          </Link>
          
          <button onClick={handleLogout} className="admin-nav-item logout">
            <span className="admin-nav-icon"><FaSignOutAlt /></span>
            <span className="admin-nav-label">Déconnexion</span>
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <button className="admin-mobile-menu-btn" onClick={() => setIsMobileMenuOpen(true)}>
            <FaBars />
          </button>
          
          <div className="admin-header-left">
            <h1>{getPageTitle()}</h1>
            <p className="admin-header-date">
              {new Date().toLocaleDateString('fr-FR', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </header>

        {/* Page Content */}
        <div className="admin-page-content">
          <Outlet />
        </div>
      </main>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div className="admin-mobile-overlay" onClick={() => setIsMobileMenuOpen(false)}></div>
      )}
    </div>
  );
};

export default AdminDashboard;